import { isTauri } from '@/tauri/utils';
import { IpcInvokeError, ipcInvokeRaw } from '@heidi/plugin-core';

export type RtfClipboardPayload = {
  html?: string;
  plainText: string;
  rtf?: string;
};

export type RtfClipboardCapabilities = {
  supported: boolean;
  protocolVersion: 2;
};

const NATIVE_ERROR_CODES = [
  'CLIPBOARD_UNAVAILABLE',
  'CLIPBOARD_FORBIDDEN_WINDOW',
  'CLIPBOARD_INVALID_PAYLOAD',
  'CLIPBOARD_UNSUPPORTED_SERIALIZATION',
  'CLIPBOARD_BUSY',
  'CLIPBOARD_NATIVE_WRITE_FAILED',
  'CLIPBOARD_INCOMPLETE_PUBLICATION',
] as const;

type NativeClipboardErrorCode = (typeof NATIVE_ERROR_CODES)[number];
export type RtfClipboardErrorCode =
  | NativeClipboardErrorCode
  | 'CLIPBOARD_REVOKED'
  | 'CLIPBOARD_SUPERSEDED';

/** Only machine codes cross into copy error reporting; never retain payload-bearing causes. */
export class RtfClipboardError extends Error {
  constructor(readonly code: RtfClipboardErrorCode) {
    super(code);
    this.name = 'RtfClipboardError';
  }
}

function sanitizeError(
  error: unknown,
  fallback: NativeClipboardErrorCode
): RtfClipboardError {
  const code = error instanceof IpcInvokeError ? error.errorCode : undefined;
  return new RtfClipboardError(
    NATIVE_ERROR_CODES.find((known) => known === code) ?? fallback
  );
}

export async function getRtfClipboardCapabilities(): Promise<RtfClipboardCapabilities> {
  if (!isTauri()) throw new RtfClipboardError('CLIPBOARD_UNAVAILABLE');
  let data: unknown;
  try {
    data = (await ipcInvokeRaw('get_rtf_clipboard_capabilities')).data as unknown;
  } catch (error) {
    throw sanitizeError(error, 'CLIPBOARD_UNAVAILABLE');
  }
  if (
    typeof data !== 'object' ||
    data === null ||
    !('supported' in data) ||
    typeof data.supported !== 'boolean' ||
    !('protocolVersion' in data) ||
    data.protocolVersion !== 2
  ) {
    throw new RtfClipboardError('CLIPBOARD_UNAVAILABLE');
  }
  return { supported: data.supported, protocolVersion: 2 };
}

export async function writeRtfClipboard(payload: RtfClipboardPayload): Promise<void> {
  if (!isTauri()) throw new RtfClipboardError('CLIPBOARD_UNAVAILABLE');
  let data: unknown;
  try {
    data = (await ipcInvokeRaw('write_rtf_clipboard', payload)).data as unknown;
  } catch (error) {
    throw sanitizeError(error, 'CLIPBOARD_NATIVE_WRITE_FAILED');
  }
  if (data !== null) throw new RtfClipboardError('CLIPBOARD_NATIVE_WRITE_FAILED');
}
